import * as React from 'react'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuList from '@mui/material/MenuList'
import { Autocomplete, MenuItem, TextField, Tooltip } from '@mui/material'
import { useConversionReport } from 'hooks/useConversionReport'

import PropTypes from 'prop-types'
import useAdsAccounts from 'queries/useAdAccounts'

const Options = ({ options, selectedIndex, handleMenuItemClick }) => {
  let content = null

  if (options?.length) {
    content = options.map(({ adAccountName: name, adAccountId: id }, index) => (
      <Tooltip key={id} title={name} placement="right-start" arrow>
        <MenuItem selected={index === selectedIndex} onClick={(event) => handleMenuItemClick(event, index)}>
          <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{name}</span>
        </MenuItem>
      </Tooltip>
    ))
  } else {
    content = (
      <MenuItem key="no-option" disabled>
        Sem opções
      </MenuItem>
    )
  }

  return content
}

export default function AccountConnectedSelect({ variant }) {
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef(null)
  const [selectedIndex, setSelectedIndex] = React.useState(-1)

  const { state, actions } = useConversionReport()
  const { data } = useAdsAccounts()

  const handleMenuItemClick = (event, index) => {
    if (index < 0 || index === null || index === undefined) return
    setSelectedIndex(index)
    const { adAccountId: id } = data[index]
    if (!id) return
    actions.setAccountId(id)
    setOpen(false)
  }

  const handleAutocompleteChange = (event, newValue) => {
    if (!newValue) return
    const { adAccountId: id } = newValue
    if (!id) return
    actions.setAccountId(id)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  if (variant === 'contained') {
    return (
      <>
        <ButtonGroup
          sx={{
            borderRadius: 10,
            height: '2.5rem',
          }}
          variant="contained"
          ref={anchorRef}
          aria-label="split button"
          data-testid="adAccount-split-button"
        >
          <Button
            sx={{
              width: '17rem',
              bgcolor: '#00AEEF',
              borderRadius: 10,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textTransform: 'uppercase',
            }}
            onClick={handleToggle}
          >
            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }} data-testid='data-testid-label-button'>
              {selectedIndex > -1 ? data[selectedIndex]?.adAccountName : 'Selecionar conta de anúncios'}
            </span>
          </Button>
          <Button
            sx={{
              bgcolor: '#00AEEF',
              borderRadius: 10,
            }}
            size="small"
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="selecione a conta de anúncio"
            aria-haspopup="menu"
            onClick={handleToggle}
            data-testid='data-testid-icon-button'
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{
            zIndex: 1,
            width: '20rem',
            height: 'auto',
            maxHeight: '12rem',
            overflowX: 'auto',
          }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          placement="bottom"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    <Options {...{ options: data, selectedIndex, handleMenuItemClick }} />
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    )
  }
  return (
    <Autocomplete
      id="adAccount-select-autocomplete"
      data-testid="adAccount-select-autocomplete"
      disableClearable
      noOptionsText="Nenhum resultado"
      value={state.adAccountId && data ? data.find((item) => item.adAccountId === state.adAccountId) : null}
      onChange={handleAutocompleteChange}
      getOptionLabel={(option) => option.adAccountName}
      options={data || []}
      sx={{
        width: '15rem',
        height: '4rem',
        color: 'default',
        '& .Mui-expanded': {
          color: '#00AEEF',
        },
        '& .Mui-focused': {
          color: '#00AEEF',
        },
        '& .MuiAutocomplete-inputFocused': {
          color: '#00AEEF',
        },
      }}
      ListboxProps={{
        style: {
          maxHeight: '12rem',
        },
      }}
      renderInput={(params) => (
        <TextField {...params} style={{ color: '#00AEEF' }} label="Selecionar conta de anúncios" variant="standard" />
      )}
    />
  )
}

AccountConnectedSelect.propTypes = {
  variant: PropTypes.oneOf(['contained', 'standard']),
}

AccountConnectedSelect.defaultProps = {
  variant: 'standard',
}
